// The review rail's cards, built from CMS testimonials. Shared by the v4
// homepage (server data) and `TestimonialsSection` (client, every main page on
// the membership-first site), so it stays free of "use client" and
// "server-only": both sides call it.
import { cmsMediaUrl, type Testimonial } from "@/lib/cms"

export type ReviewCard =
  | {
      kind: "text"
      id: string
      author: string
      headline: string
      body: string
      rating: number
      avatar?: string
      /** The review itself on Google Maps / Trustpilot, when the CMS has it. */
      href?: string
    }
  | {
      kind: "video"
      id: string
      /** Instagram creator; absent on a plain customer video, which shows
       *  `author` and `authorTitle` in the foot instead. */
      handle?: string
      followers?: string
      author: string
      authorTitle?: string
      poster: string
      preview?: string
      full?: string
      /** The creator's Instagram profile, when the CMS has it. */
      href?: string
    }

// Pierre opens the rail. The CMS order put four women in the first row
// (Nick, 09.09 daily: "we have too many women"), so the one male creator is
// pinned to the front and the rest keep their order.
const PINNED_FIRST_HANDLE = "pierrebiege"

/**
 * Text and video cards, interleaved the way the homepage frame lays them out
 * (video, video, text, video, text, then alternating), each type in the order
 * the docs arrive. The caller's query decides which docs those are: locale,
 * services, tags and audience stay the page's own.
 */
export function reviewCards(docs: Testimonial[]): ReviewCard[] {
  const videos: Extract<ReviewCard, { kind: "video" }>[] = docs
    .filter(
      (d) =>
        (d.type === "instagram-video" || d.type === "video") &&
        d.posterImage?.url
    )
    .map((d) => ({
      kind: "video",
      id: d.id,
      handle:
        d.type === "instagram-video"
          ? d.instagramHandle || undefined
          : undefined,
      followers:
        d.type === "instagram-video"
          ? d.instagramFollowers || undefined
          : undefined,
      author: d.authorName,
      authorTitle: d.authorTitle || undefined,
      poster: cmsMediaUrl(d.posterImage!.url),
      preview: d.previewVideo?.url
        ? cmsMediaUrl(d.previewVideo.url)
        : undefined,
      full: d.fullVideo?.url ? cmsMediaUrl(d.fullVideo.url) : undefined,
      href: d.instagramUrl || undefined,
    }))
  const texts: ReviewCard[] = docs
    .filter((d) => d.type === "text" && (d.headline || d.quote))
    .map((d) => ({
      kind: "text",
      id: d.id,
      author: d.authorName,
      headline: d.headline || d.quote || "",
      body: d.headline ? (d.quote ?? "") : "",
      rating: d.rating ?? 5,
      avatar: d.avatar?.url ? cmsMediaUrl(d.avatar.url) : undefined,
      href: d.externalUrl || undefined,
    }))
  const pinned = videos.findIndex((v) => v.handle === PINNED_FIRST_HANDLE)
  if (pinned > 0) videos.unshift(...videos.splice(pinned, 1))
  const pattern = ["video", "video", "text", "video", "text"] as const
  const out: ReviewCard[] = []
  let v = 0
  let t = 0
  for (let i = 0; v < videos.length || t < texts.length; i++) {
    const want = i < pattern.length ? pattern[i] : i % 2 ? "text" : "video"
    if (want === "video" && v < videos.length) out.push(videos[v++])
    else if (t < texts.length) out.push(texts[t++])
    else if (v < videos.length) out.push(videos[v++])
  }
  return out
}
